import React, { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import TopNavbar from './TopNavbar'
import MasterBanner from './MasterBanner'
import ClientChatWidget from './ClientChatWidget'
import socketService from '@/services/socketService'

const CLIENT_PORTAL_PATHS = ['/client-deliverables', '/client-queries', '/client-portal'];

const Layout: React.FC = () => {
  const [isClientUser, setIsClientUser] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check if logged-in user is a client (is_client=1 in stored user data)
    // or if they are on a client portal route
    try {
      const userStr = localStorage.getItem('crm_user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        // Check multiple paths since localStorage format changes between login and validateToken
        const isClientFlag =
          userData?.is_client === 1 ||
          userData?.data?.client?.is_client === 1 ||
          userData?.client?.is_client === 1;
        if (isClientFlag) {
          setIsClientUser(true);
          return;
        }
      }
    } catch {}
    // Fallback: detect by current route (handles existing sessions before re-login)
    const isClientRoute = CLIENT_PORTAL_PATHS.some(p => location.pathname.startsWith(p));
    setIsClientUser(isClientRoute);
  }, [location.pathname]);

  useEffect(() => {
    // Connect socket when Layout mounts (user is authenticated)
    console.log('🔌 Layout mounted, connecting socket...');
    socketService.connect();

    // Setup notification listeners
    setupNotificationListeners();

    // Cleanup on unmount (user logs out or closes tab)
    return () => {
      console.log('🔌 Layout unmounting, disconnecting socket...');
      socketService.disconnect();
    };
  }, []); // Empty dependency array = run once on mount

  /**
   * Setup listeners for real-time notifications
   */
  const setupNotificationListeners = () => {
    // Listen for new lead notifications
    socketService.on('new_lead', (data: any) => {
      console.log('📧 New lead notification received:', data);

      toast.success(
        <div>
          <strong>New Lead Assigned!</strong>
          <div className="text-sm mt-1">
            {data.lead_name} from {data.source || 'Unknown source'}
          </div>
        </div>,
        {
          autoClose: 5000,
          onClick: () => {
            window.location.href = `/leads_admin?lead_uuid=${data.lead_uuid}`;
          }
        }
      );

      // Optional: Desktop notification if tab is not active
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('New Lead Assigned!', {
          body: `${data.lead_name} from ${data.source || 'Unknown source'}`,
          icon: '/assets/dino.png',
          tag: `lead-${data.lead_id}`, // Prevent duplicates
        });
      }
    });

    // Listen for reminder notifications
    socketService.on('reminder_due', (data: any) => {
      console.log('⏰ Reminder notification received:', data);

      toast.warning(
        <div>
          <strong>Reminder Alert!</strong>
          <div className="text-sm mt-1">
            {data.event_title}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {data.event_date_time}
          </div>
        </div>,
        {
          autoClose: 8000,
          onClick: () => {
            // Navigate to activities page
            window.location.href = '/activities';
          }
        }
      );

      // Optional: Desktop notification
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Reminder Alert!', {
          body: `${data.event_title} - ${data.event_date_time}`,
          icon: '/assets/dino.png',
          tag: `reminder-${data.event_id}`,
        });
      }
    });

    // Listen for other notification types (future)
    socketService.on('lead_status_changed', (data: any) => {
      toast.info(`Lead status updated: ${data.lead_name} → ${data.new_status}`);
    });
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <MasterBanner />
      <TopNavbar />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
      {/* Floating chat widget — only for client portal users (is_client=1) */}
      {isClientUser && <ClientChatWidget />}
    </div>
  )
}

export default Layout